import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import HotelIcon from '@mui/icons-material/Hotel';
import HomeIcon from '@mui/icons-material/Home';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─── Tooltip flotante compartido ─────────────────────────────────────────────
function RoomTooltip({ tooltip }) {
  if (!tooltip) return null;
  return (
    <div style={{
      position: 'fixed',
      left: tooltip.x + 14,
      top: tooltip.y - 10,
      background: '#1a1a1a',
      color: '#fff',
      padding: '6px 12px',
      borderRadius: 8,
      fontSize: 12,
      fontFamily: 'sans-serif',
      pointerEvents: 'none',
      zIndex: 9999,
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      whiteSpace: 'nowrap',
      borderLeft: '3px solid #d4af37',
      maxWidth: 220,
    }}>
      <div style={{ fontWeight: 700, color: '#d4af37', marginBottom: 2 }}>{tooltip.roomName}</div>
      {tooltip.status === 'sold' ? (
        <span style={{ color: '#ff6b6b' }}>🔴 Vendida</span>
      ) : (
        <span style={{ opacity: 0.6, fontStyle: 'italic' }}>Disponible</span>
      )}
    </div>
  );
}

// ─── Mapa Casa Hotel para Reservas ──────────────────────────────────────────
export function CasaHotelMap({ soldRooms, idPrefix }) {
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const getStatus = (number) => {
    return soldRooms.includes(number) ? 'sold' : 'available';
  };

  const colors = {
    yellow:  { fill: '#FFF9C4', stroke: '#F9A825', text: '#5D4037' },
    green:   { fill: '#DCEDC8', stroke: '#558B2F', text: '#1B5E20' },
    blue:    { fill: '#BBDEFB', stroke: '#1565C0', text: '#0D47A1' },
    neutral: { fill: '#F5F5F5', stroke: '#BDBDBD', text: '#424242' },
    staff:   { fill: '#EEEEEE', stroke: '#9E9E9E', text: '#616161' },
    path:    { fill: '#E0E0E0', stroke: '#BDBDBD', text: '#757575' },
  };

  const sold = { fill: '#FFCDD2', stroke: '#D32F2F', text: '#B71C1C' };

  const getRoomColors = (number, baseColor) => {
    if (hovered === number) return { fill: '#FFF8E1', stroke: '#d4af37', text: '#4E342E' };
    const status = getStatus(number);
    if (status === 'sold') return sold;
    return baseColor;
  };

  const handleMouseEnter = (e, number, name) => {
    setHovered(number);
    setTooltip({
      x: e.clientX,
      y: e.clientY,
      roomName: name,
      status: getStatus(number),
    });
  };

  const handleMouseMove = (e) => {
    if (tooltip) setTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
  };

  const handleMouseLeave = () => {
    setHovered(null);
    setTooltip(null);
  };

  const Room = ({ x, y, w, h, number, name, colorKey = 'yellow', rx = 4 }) => {
    const base = colors[colorKey];
    const c = getRoomColors(number, base);
    const status = getStatus(number);
    const isSuite = name.includes('*');
    return (
      <g
        style={{ cursor: 'pointer' }}
        onMouseEnter={(e) => handleMouseEnter(e, number, name.replace('*', '') + (isSuite ? ' ★' : ''))}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <rect
          x={x} y={y} width={w} height={h} rx={rx}
          fill={c.fill}
          stroke={hovered === number ? '#d4af37' : c.stroke}
          strokeWidth={hovered === number ? 2 : 1}
          style={{ transition: 'all 0.2s' }}
        />
        {status === 'sold' && (
          <circle cx={x + w - 8} cy={y + 8} r={5} fill="#D32F2F" />
        )}
        <text
          x={x + w / 2} y={y + h / 2 - 7}
          textAnchor="middle" dominantBaseline="central"
          fontSize="11" fontWeight="600"
          fill={c.text} fontFamily="Georgia, serif"
        >
          {name}
        </text>
        <text
          x={x + w / 2} y={y + h / 2 + 8}
          textAnchor="middle" dominantBaseline="central"
          fontSize="9.5" fill={c.text} opacity={0.75} fontFamily="sans-serif"
        >
          ({number})
        </text>
      </g>
    );
  };

  const Label = ({ x, y, text, size = 10, weight = '400', color = '#757575', angle }) => (
    <text
      x={x} y={y}
      textAnchor="middle" dominantBaseline="central"
      fontSize={size} fontWeight={weight} fill={color}
      transform={angle ? `rotate(${angle}, ${x}, ${y})` : undefined}
      fontFamily="sans-serif"
      letterSpacing="0.5"
    >
      {text}
    </text>
  );

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
      <RoomTooltip tooltip={tooltip} />
      <svg id={idPrefix ? `${idPrefix}-svg-casa-hotel` : 'svg-casa-hotel'} width="100%" viewBox="0 0 660 460" xmlns="http://www.w3.org/2000/svg">
        {/* Fondo general */}
        <rect x="0" y="0" width="660" height="460" fill="#FAFAFA" rx="12" />

        {/* Título del mapa */}
        <rect x="20" y="10" width="160" height="28" rx="6" fill="#d4af37" />
        <text x="100" y="24" textAnchor="middle" dominantBaseline="central"
          fontSize="12" fontWeight="700" fill="#fff" fontFamily="Georgia, serif" letterSpacing="0.8">
          CASA HOTEL
        </text>

        {/* ── Fila superior: hab. 6-9 + ÁREA STAFF ── */}
        <Room x={185} y={10}  w={105} h={50} number={6}  name="SAN RAFAEL*"     colorKey="yellow" />
        <Room x={298} y={10}  w={105} h={50} number={7}  name="SANTA GLAFIRA*"  colorKey="green"  />
        <Room x={411} y={10}  w={105} h={50} number={8}  name="SANTA TRINIDAD*" colorKey="green"  />
        <Room x={524} y={10}  w={105} h={50} number={9}  name="SANTA SOCORRO"   colorKey="yellow" />

        {/* AREA STAFF */}
        <rect x={524} y={68} width={105} height={35} rx={4}
          fill={colors.staff.fill} stroke={colors.staff.stroke} strokeWidth={0.5} />
        <text x={576} y={85} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill={colors.staff.text} fontFamily="sans-serif">ÁREA STAFF</text>

        {/* ── Pasillo horizontal ── */}
        <rect x={185} y={68} width={335} height={30} rx={4}
          fill={colors.path.fill} stroke={colors.path.stroke} strokeWidth={0.5} />
        <Label x={352} y={83} text="PASILLO" size={9} color="#9E9E9E" />

        {/* ── Columna izquierda ── */}
        <Room x={30} y={50}  w={100} h={70} number={5}  name="SAN ALEJANDRO*" colorKey="yellow" />
        <Room x={30} y={128} w={100} h={90} number={4}  name="SANTA MARÍA*"   colorKey="yellow" />
        <Room x={30} y={226} w={100} h={70} number={3}  name="SAN CARLOS*"    colorKey="green"  />
        <Room x={30} y={304} w={100} h={80} number={1}  name="SAN JOSÉ"       colorKey="blue"   />

        {/* ── Pasillo vertical izquierdo ── */}
        <rect x={138} y={105} width={38} height={280} rx={4}
          fill={colors.path.fill} stroke={colors.path.stroke} strokeWidth={0.5} />
        <Label x={157} y={245} text="PASILLO" size={8.5} color="#9E9E9E" angle={-90} />

        {/* ── Patio Central ── */}
        <rect x={184} y={105} width={222} height={225} rx={6}
          fill="#F3F8F0" stroke="#A5D6A7" strokeWidth={1} strokeDasharray="6 3" />
        <Label x={295} y={217} text="PATIO CENTRAL" size={11} weight="600" color="#81C784" />

        {/* ── Pasillo vertical derecho ── */}
        <rect x={482} y={105} width={36} height={310} rx={4}
          fill={colors.path.fill} stroke={colors.path.stroke} strokeWidth={0.5} />
        <Label x={500} y={260} text="PASILLO" size={8.5} color="#9E9E9E" angle={-90} />

        {/* ── Columna derecha ── */}
        <Room x={524} y={110} w={105} h={48} number={10} name="SANTA ESTHER"  colorKey="blue"   />
        <Room x={524} y={165} w={105} h={48} number={11} name="SAN JORGE"     colorKey="blue"   />
        <Room x={524} y={220} w={105} h={48} number={12} name="SAN GUILLERMO" colorKey="blue"   />
        <Room x={524} y={275} w={105} h={48} number={13} name="SAN MIGUEL"    colorKey="blue"   />
        <Room x={524} y={330} w={105} h={48} number={14} name="SAN ISIDRO"    colorKey="green"  />
        <Room x={524} y={385} w={105} h={48} number={15} name="SAN PEDRO*"    colorKey="yellow" />

        {/* ── Zona inferior: Salón Chimenea + Lobby ── */}
        <rect x={185} y={337} width={90} height={48} rx={4}
          fill={colors.neutral.fill} stroke={colors.neutral.stroke} strokeWidth={0.5} />
        <text x={230} y={356} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fontWeight="600" fill="#5D4037" fontFamily="sans-serif">SALÓN</text>
        <text x={230} y={369} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill="#5D4037" fontFamily="sans-serif">CHIMENEA</text>

        <rect x={282} y={337} width={130} height={48} rx={4}
          fill={colors.neutral.fill} stroke={colors.neutral.stroke} strokeWidth={0.5} />
        <text x={347} y={356} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fontWeight="600" fill="#5D4037" fontFamily="sans-serif">LOBBY</text>
        <text x={347} y={369} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill="#5D4037" fontFamily="sans-serif">CASA HOTEL</text>

        {/* ── Leyenda ── */}
        <rect x={0} y={424} width={660} height={1} fill="#E0E0E0" />

        <rect x={20}  y={432} width={13} height={13} rx={2} fill={sold.fill} stroke={sold.stroke} strokeWidth={1} />
        <circle cx={28} cy={438} r={4} fill="#D32F2F" />
        <text x={38}  y={438} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Vendida</text>

        <rect x={130} y={432} width={13} height={13} rx={2} fill={colors.blue.fill} stroke={colors.blue.stroke} strokeWidth={1} />
        <text x={149} y={438} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Standard</text>

        <rect x={210} y={432} width={13} height={13} rx={2} fill={colors.green.fill} stroke={colors.green.stroke} strokeWidth={1} />
        <text x={229} y={438} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Junior Suite</text>

        <rect x={310} y={432} width={13} height={13} rx={2} fill={colors.yellow.fill} stroke={colors.yellow.stroke} strokeWidth={1} />
        <text x={329} y={438} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Master Suite</text>
      </svg>
    </Box>
  );
}

// ─── Mapa Hotel Boutique para Reservas ──────────────────────────────────────
export function HotelBoutiqueMap({ soldRooms, idPrefix }) {
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const getStatus = (number) => {
    return soldRooms.includes(number) ? 'sold' : 'available';
  };

  const colGreen  = { fill: '#DCEDC8', stroke: '#558B2F', text: '#1B5E20' };
  const colBlue   = { fill: '#BBDEFB', stroke: '#1565C0', text: '#0D47A1' };
  const colYellow = { fill: '#FFF9C4', stroke: '#F9A825', text: '#5D4037' };
  const sold      = { fill: '#FFCDD2', stroke: '#D32F2F', text: '#B71C1C' };
  const colPath   = { fill: '#E8E8E8', stroke: '#BDBDBD' };

  const getC = (number, base) => {
    if (hovered === number) return { fill: '#FFF8E1', stroke: '#d4af37', text: base.text };
    if (getStatus(number) === 'sold') return sold;
    return base;
  };

  const handleMouseEnter = (e, number, label) => {
    setHovered(number);
    setTooltip({
      x: e.clientX,
      y: e.clientY,
      roomName: `Hab. ${label}`,
      status: getStatus(number),
    });
  };

  const handleMouseMove = (e) => {
    if (tooltip) setTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
  };

  const handleMouseLeave = () => {
    setHovered(null);
    setTooltip(null);
  };

  const Room = ({ x, y, w, h, number, label, base, rx = 4 }) => {
    const c = getC(number, base);
    const status = getStatus(number);
    return (
      <g
        style={{ cursor: 'pointer' }}
        onMouseEnter={(e) => handleMouseEnter(e, number, label)}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <rect x={x} y={y} width={w} height={h} rx={rx}
          fill={c.fill}
          stroke={hovered === number ? '#d4af37' : c.stroke}
          strokeWidth={hovered === number ? 2 : 1}
          style={{ transition: 'all 0.2s' }}
        />
        {status === 'sold' && (
          <circle cx={x + w - 8} cy={y + 8} r={5} fill="#D32F2F" />
        )}
        <text x={x + w / 2} y={y + h / 2}
          textAnchor="middle" dominantBaseline="central"
          fontSize={11} fontWeight="600" fill={c.text} fontFamily="sans-serif">
          {label}
        </text>
      </g>
    );
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
      <RoomTooltip tooltip={tooltip} />
      <svg id={idPrefix ? `${idPrefix}-svg-hotel-boutique` : 'svg-hotel-boutique'} width="100%" viewBox="0 0 680 490" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="680" height="490" fill="#FAFAFA" rx="12" />

        {/* ══ PLANTA BAJA ══ */}
        <rect x={10} y={8} width={135} height={28} rx={6} fill="#d4af37" />
        <text x={77} y={22} textAnchor="middle" dominantBaseline="central"
          fontSize={11} fontWeight="700" fill="#fff" fontFamily="Georgia, serif" letterSpacing="0.8">
          PLANTA BAJA
        </text>
        <text x={77} y={36} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill="#888" fontFamily="sans-serif">HOTEL BOUTIQUE</text>

        {/* TERRAZA TRASERA */}
        <rect x={155} y={15} width={510} height={30} rx={4}
          fill="#FFF3E0" stroke="#FFB74D" strokeWidth={0.5} />
        <text x={410} y={30} textAnchor="middle" dominantBaseline="central"
          fontSize={9.5} fontWeight="600" fill="#E65100" fontFamily="sans-serif" letterSpacing="0.5">
          TERRAZA TRASERA
        </text>

        {/* Fila superior PB: 101-106 */}
        <Room x={155} y={52} w={170} h={48} number={101} label="101" base={colGreen} />
        <Room x={333} y={52} w={60}  h={48} number={102} label="102" base={colBlue}  />
        <Room x={401} y={52} w={80}  h={48} number={103} label="103" base={colGreen} />
        <Room x={489} y={52} w={80}  h={48} number={104} label="104" base={colGreen} />
        <Room x={577} y={52} w={52}  h={48} number={105} label="105" base={colBlue}  />
        <Room x={637} y={52} w={33}  h={48} number={106} label="106" base={colBlue}  />

        {/* Pasillo horizontal PB */}
        <rect x={155} y={106} width={515} height={28} rx={3}
          fill={colPath.fill} stroke={colPath.stroke} strokeWidth={0.5} />
        <text x={412} y={120} textAnchor="middle" dominantBaseline="central"
          fontSize={8.5} fill="#9E9E9E" fontFamily="sans-serif">PASILLO</text>

        {/* Escaleras PB */}
        <rect x={10} y={106} width={90} height={100} rx={4}
          fill="#ECEFF1" stroke="#90A4AE" strokeWidth={0.5} />
        <text x={55} y={156} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fontWeight="600" fill="#546E7A" fontFamily="sans-serif">ESCALERAS</text>

        {/* Pasillo interno */}
        <rect x={108} y={140} width={60} height={66} rx={3}
          fill={colPath.fill} stroke={colPath.stroke} strokeWidth={0.5} />
        <text x={138} y={173} textAnchor="middle" dominantBaseline="central"
          fontSize={8} fill="#9E9E9E" fontFamily="sans-serif">PASILLO</text>

        {/* Baños lobby */}
        <rect x={175} y={140} width={72} height={66} rx={3}
          fill="#F3E5F5" stroke="#CE93D8" strokeWidth={0.5} />
        <text x={211} y={165} textAnchor="middle" dominantBaseline="central"
          fontSize={8.5} fontWeight="600" fill="#6A1B9A" fontFamily="sans-serif">BAÑOS</text>
        <text x={211} y={178} textAnchor="middle" dominantBaseline="central"
          fontSize={8} fill="#6A1B9A" fontFamily="sans-serif">LOBBY</text>

        {/* Fila inferior PB: 107-110 */}
        <Room x={255} y={140} w={90}  h={66} number={110} label="110" base={colGreen} />
        <Room x={353} y={140} w={90}  h={66} number={109} label="109" base={colGreen} />
        <Room x={451} y={140} w={90}  h={66} number={108} label="108" base={colGreen} />
        <Room x={549} y={140} w={101} h={66} number={107} label="107" base={colGreen} />

        {/* Lobby */}
        <rect x={10} y={212} width={238} height={75} rx={4}
          fill="#E8F5E9" stroke="#A5D6A7" strokeWidth={0.5} />
        <text x={129} y={245} textAnchor="middle" dominantBaseline="central"
          fontSize={9.5} fontWeight="600" fill="#2E7D32" fontFamily="sans-serif">LOBBY</text>
        <text x={129} y={259} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill="#2E7D32" fontFamily="sans-serif">HOTEL BOUTIQUE</text>

        {/* Jardín terraza */}
        <rect x={255} y={212} width={395} height={75} rx={4}
          fill="#F1F8E9" stroke="#AED581" strokeWidth={0.5} strokeDasharray="5 3" />
        <text x={452} y={249} textAnchor="middle" dominantBaseline="central"
          fontSize={10} fontWeight="600" fill="#558B2F" fontFamily="sans-serif">JARDÍN TERRAZA CENTRAL</text>

        {/* ══ PLANTA ALTA ══ */}
        <rect x={10} y={302} width={135} height={28} rx={6} fill="#8D6E63" />
        <text x={77} y={316} textAnchor="middle" dominantBaseline="central"
          fontSize={11} fontWeight="700" fill="#fff" fontFamily="Georgia, serif" letterSpacing="0.8">
          PLANTA ALTA
        </text>
        <text x={77} y={330} textAnchor="middle" dominantBaseline="central"
          fontSize={9} fill="#D7CCC8" fontFamily="sans-serif">HOTEL BOUTIQUE</text>

        {/* Fila superior PA: 200-205 */}
        <Room x={155} y={298} w={82} h={50} number={200} label="200" base={colGreen} />
        <Room x={245} y={298} w={82} h={50} number={201} label="201" base={colGreen} />
        <Room x={335} y={298} w={82} h={50} number={202} label="202" base={colGreen} />
        <Room x={425} y={298} w={82} h={50} number={203} label="203" base={colGreen} />
        <Room x={515} y={298} w={82} h={50} number={204} label="204" base={colGreen} />
        <Room x={605} y={298} w={65} h={50} number={205} label="205" base={colGreen} />

        {/* Escaleras PA */}
        <rect x={10} y={354} width={90} height={32} rx={3}
          fill="#ECEFF1" stroke="#90A4AE" strokeWidth={0.5} />
        <text x={55} y={370} textAnchor="middle" dominantBaseline="central"
          fontSize={8.5} fontWeight="600" fill="#546E7A" fontFamily="sans-serif">ESCALERAS</text>

        {/* Pasillo PA */}
        <rect x={108} y={354} width={562} height={32} rx={3}
          fill={colPath.fill} stroke={colPath.stroke} strokeWidth={0.5} />
        <text x={389} y={370} textAnchor="middle" dominantBaseline="central"
          fontSize={8.5} fill="#9E9E9E" fontFamily="sans-serif">PASILLO</text>

        {/* Fila inferior PA: 206-210 — todas amarillas Master Suite */}
        <Room x={155} y={394} w={100} h={48} number={210} label="210 ★" base={colYellow} />
        <Room x={263} y={394} w={100} h={48} number={209} label="209 ★" base={colYellow} />
        <Room x={371} y={394} w={100} h={48} number={208} label="208 ★" base={colYellow} />
        <Room x={479} y={394} w={100} h={48} number={207} label="207 ★" base={colYellow} />
        <Room x={585} y={394} w={85}  h={48} number={206} label="206 ★" base={colYellow} />

        {/* ── Leyenda ── */}
        <rect x={0} y={454} width={680} height={1} fill="#E0E0E0" />

        <rect x={10}  y={462} width={13} height={13} rx={2} fill={sold.fill} stroke={sold.stroke} strokeWidth={1} />
        <circle cx={18} cy={468} r={4} fill="#D32F2F" />
        <text x={29}  y={468} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Vendida</text>

        <rect x={120} y={462} width={13} height={13} rx={2} fill={colBlue.fill}   stroke={colBlue.stroke}   strokeWidth={1} />
        <text x={139} y={468} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Standard (Queen)</text>

        <rect x={270} y={462} width={13} height={13} rx={2} fill={colGreen.fill}  stroke={colGreen.stroke}  strokeWidth={1} />
        <text x={289} y={468} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Junior Suite (King)</text>

        <rect x={420} y={462} width={13} height={13} rx={2} fill={colYellow.fill} stroke={colYellow.stroke} strokeWidth={1} />
        <text x={439} y={468} fontSize={9} fill="#424242" fontFamily="sans-serif" dominantBaseline="central">Master Suite ★ (4 pax)</text>
      </svg>
    </Box>
  );
}

export default function RoomMapModal({ open, onClose, checkIn, checkOut }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [soldRooms, setSoldRooms] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('boutique');

  useEffect(() => {
    if (open && checkIn && checkOut) {
      fetchSoldRooms();
    }
  }, [open, checkIn, checkOut]);

  const fetchSoldRooms = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_URL}/api/bookings?checkIn=${checkIn}&checkOut=${checkOut}`,
        {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );
      if (!response.ok) throw new Error('Error al cargar reservas');
      const bookings = await response.json();

      // Parsear habitaciones vendidas
      const sold = [];
      bookings.forEach(booking => {
        if (booking.roomName && booking.roomName.includes(' - ')) {
          const parts = booking.roomName.split(' - ');
          const roomName = parts[1];
          // Extraer número de habitación
          const match = roomName.match(/(\d+)/);
          if (match) {
            sold.push(parseInt(match[1]));
          }
        }
      });
      setSoldRooms([...new Set(sold)]); // Eliminar duplicados
    } catch (err) {
      console.error('Error cargando habitaciones vendidas:', err);
      setError('No se pudieron cargar las habitaciones vendidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', bgcolor: '#d4af37', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <MapIcon />
          <Typography variant="h6">Mapa de Habitaciones Vendidas</Typography>
        </Box>
        {checkIn && checkOut && (
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Del {new Date(checkIn).toLocaleDateString('es-ES')} al {new Date(checkOut).toLocaleDateString('es-ES')}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <ToggleButtonGroup
            value={selectedHotel}
            exclusive
            onChange={(e, value) => value && setSelectedHotel(value)}
            sx={{ width: '100%', justifyContent: 'center' }}
          >
            <ToggleButton value="boutique" sx={{ flex: 1 }}>
              <HotelIcon sx={{ mr: 1 }} />
              Hotel Boutique
            </ToggleButton>
            <ToggleButton value="casaHotel" sx={{ flex: 1 }}>
              <HomeIcon sx={{ mr: 1 }} />
              Casa Hotel
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : (
          <Box sx={{ p: 2 }}>
            {selectedHotel === 'boutique' ? (
              <HotelBoutiqueMap soldRooms={soldRooms} />
            ) : (
              <CasaHotelMap soldRooms={soldRooms} />
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}